import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { uploadFile } from "@/components/UploadButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronUp, ChevronDown, FileText, Upload, Loader2, Trash2, Sparkles, AlertTriangle, History, GitCompare, ExternalLink, CheckCircle2, Lock, LockOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "grille_classification", label: "docGrilleClassification" },
  { key: "plan_contingence", label: "docPlanContingence" },
  { key: "plan_intervention", label: "docPlanIntervention" },
  { key: "autre", label: "docAutre" },
];
const ACCEPT = "application/pdf,image/*,.doc,.docx";
const fmtDate = (s) => { try { return new Date(s).toLocaleString("fr-CA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

function StatusBadge({ status, t }) {
  if (status === "done") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><Sparkles className="w-3 h-3" />{t("docReady")}</Badge>;
  if (status === "empty") return <Badge variant="outline" className="gap-1 text-muted-foreground">{t("docEmpty")}</Badge>;
  if (status === "locked") return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 gap-1"><Lock className="w-3 h-3" />{t("docLocked")}</Badge>;
  if (status === "failed") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1"><AlertTriangle className="w-3 h-3" />{t("docFailed")}</Badge>;
  return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1"><Loader2 className="w-3 h-3 animate-spin" />{t("docProcessing")}</Badge>;
}

function UnlockDialog({ open, onClose, residentId, docId, versionId, t, onDone }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setPw(""); }, [open]);
  const submit = async (e) => {
    e?.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await api.post(`/residents/${residentId}/documents/${docId}/versions/${versionId}/unlock`, { password: pw });
      toast.success(t("docReady"));
      onDone();
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || t("wrongPassword")); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="unlock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5" />{t("unlock")}</DialogTitle>
          <DialogDescription>{t("pdfPasswordPrompt")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="unlock-password" />
          <Button type="submit" className="w-full" disabled={loading || !pw} data-testid="unlock-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <LockOpen className="w-4 h-4 mr-1.5" />}{t("unlockAndRead")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ open, onClose, doc, residentId, t }) {
  const versions = doc?.versions || [];
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && versions.length >= 2) {
      setTo(doc.current_version || versions[0].version_id);
      const other = versions.find((v) => v.version_id !== (doc.current_version || versions[0].version_id));
      setFrom(other?.version_id || versions[1].version_id);
      setResult(null);
    }
  }, [open]); // eslint-disable-line

  const vLabel = (v) => `v${v.version_no} · ${fmtDate(v.created_at)}`;
  const run = async () => {
    if (from === to) { toast.error(t("compareSameVersion")); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/residents/${residentId}/documents/${doc.id}/compare`, { from_version: from, to_version: to });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="w-5 h-5" />{t("compareVersions")}</DialogTitle>
          <DialogDescription>{doc?.name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t("versionOlder")}</label>
            <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="compare-from" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              {versions.map((v) => <option key={v.version_id} value={v.version_id}>{vLabel(v)}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">{t("versionNewer")}</label>
            <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="compare-to" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              {versions.map((v) => <option key={v.version_id} value={v.version_id}>{vLabel(v)}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={run} disabled={loading} data-testid="compare-run">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}{t("compareWithAi")}
          </Button>
          {from && <a href={fileDownloadUrl(versions.find((v) => v.version_id === from)?.file_id)} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2.5 py-2 rounded-md border border-border hover:border-primary"><ExternalLink className="w-3.5 h-3.5" />{t("openOlder")}</a>}
          {to && <a href={fileDownloadUrl(versions.find((v) => v.version_id === to)?.file_id)} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-2.5 py-2 rounded-md border border-border hover:border-primary"><ExternalLink className="w-3.5 h-3.5" />{t("openNewer")}</a>}
        </div>
        {result && (
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm whitespace-pre-wrap leading-relaxed [overflow-wrap:anywhere]" data-testid="compare-result">
            {result.comparison}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocumentRow({ doc, residentId, t, onChanged }) {
  const [open, setOpen] = useState(false);
  const [compare, setCompare] = useState(false);
  const [unlockVid, setUnlockVid] = useState(null);
  const versions = doc.versions || [];
  const cur = versions.find((v) => v.version_id === doc.current_version) || versions[0];

  const del = async () => {
    if (!window.confirm(t("deleteDocConfirm"))) return;
    try { await api.delete(`/residents/${residentId}/documents/${doc.id}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delVersion = async (vid) => {
    if (!window.confirm(t("deleteVersionConfirm"))) return;
    try { await api.delete(`/residents/${residentId}/documents/${doc.id}/versions/${vid}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-md border border-border" data-testid={`doc-${doc.id}`}>
      <div className="flex items-center gap-2 px-2.5 py-2">
        <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
        <a href={fileDownloadUrl(cur?.file_id)} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm font-medium hover:underline">{doc.name}</a>
        <span className="text-xs text-muted-foreground hidden sm:inline">v{cur?.version_no}</span>
        <StatusBadge status={cur?.extract_status} t={t} />
        {cur?.extract_status === "locked" && (
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setUnlockVid(cur.version_id)} data-testid={`unlock-open-${doc.id}`}><LockOpen className="w-3.5 h-3.5" />{t("unlock")}</Button>
        )}
        {versions.length >= 2 && (
          <button onClick={() => setCompare(true)} title={t("compareVersions")} className="text-muted-foreground hover:text-primary" data-testid={`compare-open-${doc.id}`}><GitCompare className="w-4 h-4" /></button>
        )}
        <button onClick={() => setOpen((o) => !o)} title={t("history")} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5" data-testid={`history-toggle-${doc.id}`}>
          <History className="w-4 h-4" />{versions.length > 1 && <span className="text-[10px]">{versions.length}</span>}
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <button onClick={del} className="text-muted-foreground hover:text-destructive" data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="border-t border-border px-2.5 py-2 space-y-1.5 bg-muted/30" data-testid={`versions-${doc.id}`}>
          {versions.slice().sort((a, b) => b.version_no - a.version_no).map((v) => (
            <div key={v.version_id} className="flex items-center gap-2 text-xs" data-testid={`version-${v.version_id}`}>
              <span className="font-semibold w-8">v{v.version_no}</span>
              {v.version_id === doc.current_version && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 gap-1"><CheckCircle2 className="w-3 h-3" />{t("currentVersion")}</Badge>}
              <a href={fileDownloadUrl(v.file_id)} target="_blank" rel="noreferrer" className="hover:underline truncate max-w-[120px]">{v.name}</a>
              <span className="text-muted-foreground">{fmtDate(v.created_at)}</span>
              <span className="text-muted-foreground hidden sm:inline">· {v.uploaded_by_name}</span>
              <StatusBadge status={v.extract_status} t={t} />
              {v.extract_status === "locked" && (
                <button onClick={() => setUnlockVid(v.version_id)} title={t("unlock")} className="text-orange-600 hover:text-orange-700" data-testid={`unlock-version-${v.version_id}`}><LockOpen className="w-3.5 h-3.5" /></button>
              )}
              <button onClick={() => delVersion(v.version_id)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`delete-version-${v.version_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <CompareDialog open={compare} onClose={() => setCompare(false)} doc={doc} residentId={residentId} t={t} />
      <UnlockDialog open={!!unlockVid} onClose={() => setUnlockVid(null)} residentId={residentId} docId={doc.id} versionId={unlockVid} t={t} onDone={onChanged} />
    </div>
  );
}

export default function ResidentDocuments({ residentId, isManager }) {
  const { t } = useT();
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);
  const pendingRef = useRef(null); // {type:'new'|'version', category?, docId?}
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/residents/${residentId}/documents`); setDocs(data); }
    catch { /* non-managers 403 */ }
  }, [residentId]);

  useEffect(() => { if (isManager && residentId) load(); }, [isManager, residentId, load]);

  useEffect(() => {
    const processing = docs.some((d) => (d.versions || []).some((v) => v.extract_status === "processing"));
    if (processing) { pollRef.current = setTimeout(load, 4000); return () => clearTimeout(pollRef.current); }
  }, [docs, load]);

  if (!isManager) return null;

  const triggerNew = (category) => { pendingRef.current = { type: "new", category }; fileRef.current?.click(); };
  const triggerVersion = (docId) => { pendingRef.current = { type: "version", docId }; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    const action = pendingRef.current; pendingRef.current = null;
    if (!file || !action) return;
    setBusy(action.type === "new" ? `cat-${action.category}` : `doc-${action.docId}`);
    try {
      const up = await uploadFile(file, "resident-doc");
      if (action.type === "new")
        await api.post(`/residents/${residentId}/documents`, { file_id: up.id, category: action.category, name: file.name });
      else
        await api.post(`/residents/${residentId}/documents/${action.docId}/versions`, { file_id: up.id, name: file.name });
      toast.success(t("docProcessing"));
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="rsection-documents">
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="font-display text-lg font-bold text-primary flex items-center gap-2"><FileText className="w-5 h-5" />{t("classifiedDocs")}</h2>
        <button onClick={() => setOpen((o) => !o)} className="w-7 h-7 rounded-full hover:bg-accent flex items-center justify-center">
          <ChevronUp className={`w-4 h-4 transition-transform ${open ? "" : "rotate-180"}`} />
        </button>
      </div>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />{t("docsAiHint")}</p>
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} />
          {CATEGORIES.map((cat) => {
            const items = docs.filter((d) => d.category === cat.key);
            return (
              <div key={cat.key} className="rounded-lg border border-border p-3" data-testid={`doccat-${cat.key}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold">{t(cat.label)}</h3>
                  <Button size="sm" variant="outline" disabled={busy === `cat-${cat.key}`} onClick={() => triggerNew(cat.key)} data-testid={`upload-doc-${cat.key}`}>
                    {busy === `cat-${cat.key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span className="ml-1.5">{t("uploadFileFor")}</span>
                  </Button>
                </div>
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("noDocsCategory")}</p>
                ) : (
                  <div className="space-y-2">
                    {items.map((d) => (
                      <div key={d.id}>
                        <DocumentRow doc={d} residentId={residentId} t={t} onChanged={load} />
                        <div className="flex justify-end mt-1">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy === `doc-${d.id}`} onClick={() => triggerVersion(d.id)} data-testid={`new-version-${d.id}`}>
                            {busy === `doc-${d.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}{t("newVersion")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
